import Link from "next/link";

import { SponsoredCta } from "@/components/social/SponsoredCta";
import { formatMoney } from "@/lib/format";
import type { DemandPost, Expert, Listing, MarketConfig, ServiceProvider, SocialPost } from "@/lib/types";

export function ListingCard({ listing, market }: { listing: Listing; market: MarketConfig }) {
  return (
    <Link href={`/listings/${listing.id}`} className="listing-card">
      <div className="media media-4x3">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={listing.imageUrl} alt={listing.title} />
        <span className="badge">{listing.grade}</span>
      </div>
      <div className="card-body">
        <div className="eyebrow">{listing.category}</div>
        <h3>{listing.title}</h3>
        <p>{listing.quantity.toLocaleString()} {listing.unit} available in {listing.region}</p>
        <div className="card-meta">
          <b>{formatMoney(listing.price, market)}</b>
          <span>/{listing.unit}</span>
        </div>
      </div>
    </Link>
  );
}

export function DemandCard({ demand, market }: { demand: DemandPost; market: MarketConfig }) {
  return (
    <article className="demand-card">
      <div>
        <div className="eyebrow">Buyer need</div>
        <h3>{demand.title}</h3>
        <p>{demand.buyerType} in {demand.region} needs {demand.quantity.toLocaleString()} {demand.unit} by {demand.neededBy}.</p>
      </div>
      <div className="demand-foot">
        <span>Up to <b>{formatMoney(demand.maxPrice, market)}</b> / {demand.unit}</span>
        <Link href={`/buyer-demand/${demand.id}`} className="link-pill">
          I can supply
        </Link>
      </div>
    </article>
  );
}

export function PostCard({ post }: { post: SocialPost }) {
  const createdAt = formatPostDate(post.createdAt);
  const mediaPreview = post.imageUrls[0] ?? post.videoThumbnailUrl;

  // Sponsored campaign items are not real posts: no detail page, no
  // like/comment counts — a labeled card with a tracked CTA instead.
  if (post.isSponsored) {
    return (
      <article className="post-card">
        <div className="post-author">
          {post.authorAvatarUrl ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={post.authorAvatarUrl} alt="" />
          ) : (
            <span>{initials(post.sponsorName ?? post.authorDisplayName)}</span>
          )}
          <div>
            <h3>{post.sponsorName ?? post.authorDisplayName}</h3>
            <p>{post.sponsoredLabel ?? "Sponsored"}</p>
          </div>
        </div>
        {post.text ? <p className="post-text">{post.text}</p> : null}
        {mediaPreview ? (
          <div className="media post-media">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src={mediaPreview} alt="" />
            <span className="badge">{post.sponsoredLabel ?? "Sponsored"}</span>
          </div>
        ) : null}
        <div className="inline-meta">
          <span>{post.sponsoredLabel ?? "Sponsored"}</span>
          {post.campaignId ? (
            <SponsoredCta
              campaignId={post.campaignId}
              label={post.sponsoredCtaLabel ?? "Message business"}
              clickToken={post.clickToken ?? undefined}
              targetUserId={post.authorId}
              contextTitle={post.sponsoredProductName ?? post.sponsorName ?? post.authorDisplayName}
            />
          ) : null}
        </div>
      </article>
    );
  }

  return (
    <article className="post-card">
      <div className="post-author">
        {post.authorAvatarUrl ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={post.authorAvatarUrl} alt="" />
        ) : (
          <span>{initials(post.authorDisplayName)}</span>
        )}
        <div>
          <h3>{post.authorDisplayName}</h3>
          <p>{[post.authorRole, post.locationName, createdAt].filter(Boolean).join(" - ")}</p>
        </div>
      </div>
      {post.text ? <p className="post-text">{post.text}</p> : null}
      {post.videoUrl ? (
        <div className="media post-media">
          <video controls poster={post.videoThumbnailUrl ?? undefined} preload="metadata" src={post.videoUrl} />
          <span className="badge">Video</span>
        </div>
      ) : mediaPreview ? (
        <Link
          href={`/posts/${post.id}`}
          className="media post-media post-media-link"
          aria-label={`Open post by ${post.authorDisplayName}`}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={mediaPreview} alt="" />
        </Link>
      ) : null}
      {post.hashtags.length ? (
        <div className="tag-row">
          {post.hashtags.slice(0, 4).map((tag) => (
            <span key={tag} className="tag">#{tag}</span>
          ))}
        </div>
      ) : null}
      <div className="inline-meta">
        <span>{post.likesCount.toLocaleString()} likes</span>
        <span>{post.commentsCount.toLocaleString()} comments</span>
        <span>{post.sharesCount.toLocaleString()} shares</span>
        <Link href={`/posts/${post.id}#comments`} className="link-pill">
          Comment
        </Link>
        <Link href={`/posts/${post.id}`} className="link-pill">
          Open post
        </Link>
      </div>
    </article>
  );
}

export function ProviderCard({ provider }: { provider: ServiceProvider }) {
  const href = provider.type === "agrovet" ? `/services/agrovet/${provider.id}` : `/providers/${provider.id}`;
  return (
    <Link href={href} className="provider-card">
      <div className="media provider-media">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={provider.imageUrl} alt={provider.name} />
      </div>
      <div>
        <div className="eyebrow">{provider.type}</div>
        <h3>{provider.name}</h3>
        <p>{provider.headline}</p>
        <div className="inline-meta">
          <span>{provider.region}</span>
          <span>{provider.rating.toFixed(1)} rating</span>
          <span>{provider.availability}</span>
        </div>
      </div>
    </Link>
  );
}

export function ExpertCard({ expert, market }: { expert: Expert; market: MarketConfig }) {
  return (
    <Link href={`/experts/${expert.id}`} className="expert-card">
      <div className="media expert-media">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src={expert.imageUrl} alt={expert.name} />
      </div>
      <div className="card-body">
        <div className="eyebrow">Expert advisor</div>
        <h3>{expert.name}</h3>
        <p>{expert.title}</p>
        <div className="card-meta">
          <b>{formatMoney(expert.fee, market)}</b>
          <span>{expert.videoSupported ? "Video supported" : "Chat only"}</span>
        </div>
      </div>
    </Link>
  );
}

function initials(name: string) {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function formatPostDate(value?: string | null) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const diffMs = Date.now() - date.getTime();
  const hours = Math.floor(diffMs / 3_600_000);
  if (hours < 1) return "Just now";
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days`;
  return new Intl.DateTimeFormat("en", { month: "short", day: "numeric" }).format(date);
}
